// Third-party
import React, { ReactElement } from 'react';
import debounce from 'lodash/debounce';
import { Waypoint } from 'react-waypoint';
import { connect } from 'react-redux';

// Components
import ActionModal from './ActionModal';
import DataSourceTabs from '@/views/components/data-source/DataSourceTabs';
import EditContactModal from './EditContactModal';
import { Input, SearchSelectInput, SelectOption } from '../inputs';
import { ContactPastille } from '../misc';
import { SecondaryLink } from '../buttons';
import { LoadingSpinner } from '../common';

// Enums
import { DATA_SOURCE_TAB_CATEGORY } from '@/enums/DataSource';

// Utils
import { formatLocalizedString, formatMessage, FormattedMessage, LOCALE } from '../../../utils/i18n';
import { getContactName } from '../../../utils/contacts';
import { deepClone } from '../../../utils/objects';
import { resetLocalStoragePersistenceSource } from '@/utils/persistence';
import localStorageUtils from '@/utils/local-storage';
import { getFilteredDataSourceTabs } from '@/utils/data-source';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import { getMyContacts, setPersistenceSource, GET_CONTACTS } from '../../../redux/modules/contacts';
import { startLoaderAction, selectIsLoadingEvery } from '../../../redux/modules/ui/loaders';
import { setIsActiveContactPostMessage } from '@/redux/modules/post-message';

// Types
import GenericContact from '@/types/generic-contact';
import User from '@/types/user';
import Portfolio from '@/types/portfolio';
import LocalizedString from '@/types/localized-string';
import Nullable from '@/types/nullable';
import { DataSourceTabs as DataSourceTabsType } from '@/types/app-config';
import { SEARCH_DEBOUNCE_TIME } from '@/utils/debounce/constants';

type StateProps = {
  isPostMessageActive: boolean;
  contacts: GenericContact[];
  loadingContacts: boolean;
  nextContactsToken?: string;
};

type DispatchProps = {
  getMyContacts: (...args: Parameters<typeof getMyContacts>) => Promise<GenericContact[]>;
  startLoaderAction: (name: string) => void;
  setIsActiveContactPostMessage: (isActive: boolean) => void;
  setPersistenceSource: (user: User, key: string, value?: string) => void;
};

export type Props = StateProps &
  DispatchProps & {
    show: boolean;
    user: User;
    onCancel: () => void;
    onSave: (portfolioId: string | undefined, portfolio: Portfolio, contact?: GenericContact) => void;
    portfolio: Portfolio;
    requireContact?: boolean;
    currentContact?: GenericContact;
    loadingPortfolios?: boolean;
  };

type State = {
  activeDataSource: string;
  portfolioName: LocalizedString;
  selectedContact?: GenericContact;
  showCreateContact: boolean;
  contactsSearchQuery: string;
};

class AssignPortfolioModal extends React.Component<Props> {
  private dataSourceTabs: DataSourceTabsType[] | null = getFilteredDataSourceTabs(
    this.props.user,
    DATA_SOURCE_TAB_CATEGORY.CONTACTS,
    this.props.isPostMessageActive,
  );

  private nameInput = React.createRef<Input>();

  private isPostMessageActive: boolean = this.props.isPostMessageActive;

  readonly state: State = {
    activeDataSource: this.dataSourceTabs?.[0]?.value ?? '',
    portfolioName: this.props.portfolio.name,
    selectedContact: this.getSelectedContact(),
    showCreateContact: false,
    contactsSearchQuery: '',
  };

  debouncedSearchContacts = debounce(() => {
    const { contactsSearchQuery } = this.state;
    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: undefined });
  }, SEARCH_DEBOUNCE_TIME);

  componentDidMount() {
    const { isPostMessageActive, user, getMyContacts } = this.props;

    // Reset the Persistence Source when the component mounts
    const isPostMessageDataSourceTabExist = this.dataSourceTabs?.find(tab => tab.isPostMessage);
    const isInitialDataSourceTabPostMessage = isPostMessageActive && isPostMessageDataSourceTabExist;

    if (isInitialDataSourceTabPostMessage) {
      setIsActiveContactPostMessage(false);
    }
    resetLocalStoragePersistenceSource(localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE, user, () => getMyContacts());

    if (this.nameInput.current) {
      this.nameInput.current.selectAll();
    }
  }

  getSelectedContact() {
    const { portfolio, contacts } = this.props;

    if (!portfolio.contact) {
      return undefined;
    }

    return contacts.find(c => c._id === portfolio.contact!._id);
  }

  onConfirm = () => {
    const { selectedContact, portfolioName } = this.state;
    const { portfolio, currentContact, requireContact } = this.props;
    let contact: GenericContact | undefined;
    if (requireContact) {
      contact = currentContact !== undefined ? currentContact : selectedContact;
    }
    const newPortfolio = deepClone(portfolio);
    newPortfolio._id = undefined;
    newPortfolio.name = portfolioName;
    if (newPortfolio.riskProfile) {
      newPortfolio.riskProfile._id = undefined;
    }
    if (!contact) {
      newPortfolio.mine = true;
    }

    this.props.onSave(portfolio._id, newPortfolio, contact);
  };

  onContactChange = (contact?: Nullable<GenericContact>) => {
    if (contact !== null) {
      this.setState({ selectedContact: contact });
    } else {
      this.showCreateContactModal();
    }
  };

  onPortfolioNameChange = (value: string) => {
    const portfolioName = {
      [LOCALE.EN_CA]: value,
      [LOCALE.FR_CA]: value,
    };
    this.setState({ portfolioName });
  };

  showCreateContactModal = () => {
    this.setState({ showCreateContact: true });
  };

  onCancelCreateContact = () => {
    this.setState({ showCreateContact: false });
  };

  onCreateContact = (contact: GenericContact) => {
    this.setState({
      selectedContact: contact,
      showCreateContact: false,
    });
  };

  getSaveLabel = () => {
    const { requireContact } = this.props;

    if (requireContact) {
      return formatMessage('AssignPortfolioModal.Action.SaveToContact');
    }

    return formatMessage('AssignPortfolioModal.Action.SaveAsModel');
  };

  getModalTitle = () => {
    const { requireContact, currentContact } = this.props;

    if (requireContact) {
      if (currentContact != null) {
        const contact = `${currentContact.fname} ${currentContact.lname}`;
        return formatMessage('AssignPortfolioModal.Title.ToCurrentContact', { contact });
      }
      return formatMessage('AssignPortfolioModal.Title.ToContact');
    }

    return formatMessage('AssignPortfolioModal.Title.AsModel');
  };

  getAdditionalDropdownActionsBefore = () => {
    const { selectedContact } = this.state;
    const { user } = this.props;
    const actions = [];

    const removeContactOption = (
      <SelectOption key="remove-contact" value={undefined} className="d-flex align-items-center">
        <SecondaryLink noBorder>
          <span className="remove-contact">
            <FormattedMessage id="AssignPortfolioModal.RemoveContact" />
          </span>
        </SecondaryLink>
      </SelectOption>
    );

    if (selectedContact) {
      actions.push(removeContactOption);
    }

    const hasPersistenceKeySource = localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE) != null;

    if (!this.isPostMessageActive && !hasPersistenceKeySource) {
      const createContactOption = (
        <SelectOption key="new-contact" value={null} className="d-flex align-items-center">
          <span className="contact-name">
            <FormattedMessage id="AssignPortfolioModal.CreateContact" />
          </span>
        </SelectOption>
      );

      actions.push(createContactOption);
    }

    return actions;
  };

  onContactsSearchChange = (contactsSearchQuery: string) => {
    this.setState({ contactsSearchQuery });
    this.props.startLoaderAction(GET_CONTACTS.type);

    this.debouncedSearchContacts();
  };

  onLoadMoreContacts = () => {
    const { contactsSearchQuery } = this.state;
    const { nextContactsToken } = this.props;

    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: nextContactsToken });
  };

  getAdditionalDropdownActionsAfter = () => {
    const { nextContactsToken } = this.props;

    const options = [];

    if (nextContactsToken != null) {
      options.push(
        <>
          <LoadingSpinner />
          <Waypoint onEnter={this.onLoadMoreContacts} />
        </>,
      );
    }

    return options;
  };

  confirmIsDisabled = () => {
    const { requireContact, currentContact } = this.props;
    const { selectedContact, portfolioName } = this.state;

    if (requireContact && selectedContact == null && currentContact == null) {
      return true;
    }

    return !formatLocalizedString(portfolioName).trim();
  };

  onSelectDropdown = (contact: GenericContact) => {
    this.onContactChange(contact);
  };

  filterContact = (option: ReactElement, query: string): boolean => {
    const contact: GenericContact = option.props.value;
    let result = false;

    if (contact) {
      const re = new RegExp(query, 'gi');
      result = !!getContactName(contact)?.match(re)?.length;
    }

    return result;
  };

  onSelectDataSource = (dataSourceValue: string) => {
    const { user, setIsActiveContactPostMessage, setPersistenceSource } = this.props;

    if (!this.dataSourceTabs) return;

    this.setState({ activeDataSource: dataSourceValue });

    const activeDataSource = this.dataSourceTabs.find(tab => tab.value === dataSourceValue);
    let persistenceSource;

    if (activeDataSource?.isPostMessage) {
      setIsActiveContactPostMessage(true);
    } else {
      setIsActiveContactPostMessage(false);
      persistenceSource = activeDataSource?.persistenceKey;
    }

    setPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE, persistenceSource);
    this.debouncedSearchContacts();
  };

  render() {
    const { onSave, contacts, requireContact, currentContact, user, loadingContacts, loadingPortfolios, ...rest } = this.props;
    const { activeDataSource, portfolioName } = this.state;

    const toggle = this.dataSourceTabs ? (
      <DataSourceTabs disabled={loadingPortfolios} tabs={this.dataSourceTabs} value={activeDataSource} onChange={this.onSelectDataSource} />
    ) : undefined;

    return (
      <>
        <ActionModal
          className="assign-portfolio-modal"
          onConfirm={this.onConfirm}
          confirmLabel={this.getSaveLabel()}
          confirmIsDisabled={this.confirmIsDisabled()}
          {...rest}
        >
          <h3>{this.getModalTitle()}</h3>

          <Input
            data-private
            data-dd-privacy="mask"
            ref={this.nameInput}
            value={formatLocalizedString(portfolioName)}
            onValueChange={this.onPortfolioNameChange}
            placeholder={formatMessage('AssignPortfolioModal.NamePlaceholder')}
          />

          {requireContact && (
            <SearchSelectInput
              onSelect={this.onSelectDropdown}
              data-private
              data-dd-privacy="mask"
              placeholder={formatMessage('Comparison.SaveComparisonModal.ChooseContact')}
              filterOption={this.filterContact}
              additionnalOptionsBefore={this.getAdditionalDropdownActionsBefore()}
              additionnalOptionsAfter={this.getAdditionalDropdownActionsAfter()}
              selectedOptionValue={this.state.selectedContact}
              getTextForOptionValue={getContactName}
              onSearch={this.onContactsSearchChange}
              loadingResults={loadingContacts}
              toggleControl={toggle}
            >
              {contacts.map(c => (
                <SelectOption key={c._id} value={c} className="d-flex align-items-center">
                  <ContactPastille contact={c} />
                  <span className="contact-name">{getContactName(c)}</span>
                </SelectOption>
              ))}
            </SearchSelectInput>
          )}
        </ActionModal>

        <EditContactModal user={user} show={this.state.showCreateContact} onCancel={this.onCancelCreateContact} onSave={this.onCreateContact} />
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  contacts: state.contacts.contacts,
  nextContactsToken: state.contacts.nextContactsToken,
  loadingContacts: selectIsLoadingEvery(state, [GET_CONTACTS.type]),
  isPostMessageActive: state.postMessage.isPostMessageActive,
});

export default connect(mapStateToProps, {
  getMyContacts,
  startLoaderAction,
  setIsActiveContactPostMessage,
  setPersistenceSource,
})(AssignPortfolioModal);
